import * as React from 'react'

import { Tag } from '@factorialco/deprecated-design-system'
import {
  AmountStrategyTagFragment,
  ContractsValueObjectsAmountStrategyTypeEnum,
  PayrollValueObjectsConceptUnitTypeEnum,
} from 'generated/resources/graphql'

import { fromCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { PER_WORKED_DAY_STRATEGY } from 'modules/contracts/lib/additionalCompensationStrategies'
import { isGraphQLType } from 'modules/contracts/lib/types'

type Props = {
  amountStrategyType: ContractsValueObjectsAmountStrategyTypeEnum
  amountStrategy: AmountStrategyTagFragment
  unitType: PayrollValueObjectsConceptUnitTypeEnum
  unit: string
}

function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60

  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`
  } else if (hours > 0) {
    return `${hours}h`
  } else {
    return `${mins}m`
  }
}

function formatAmount(
  amount: number,
  unitType: PayrollValueObjectsConceptUnitTypeEnum,
  unit: string
): string {
  if (unitType === 'money') {
    return String(fromCents(amount))
  } else if (unit === 'mins') {
    return formatMinutes(amount)
  }

  return String(amount)
}

function AmountStrategyTag({ amountStrategyType, amountStrategy, unitType, unit }: Props) {
  let text = ''

  if (
    amountStrategyType === 'contracts_fixed_amount_strategy' &&
    isGraphQLType(amountStrategy, 'ContractsFixedAmountStrategy')
  ) {
    const prefix = i18n.t(
      'contract_versions.additional_compensation.amount_strategy_tag.fixed_amount_strategy'
    )

    if (amountStrategy.amount != null) {
      const formattedAmount = formatAmount(amountStrategy.amount, unitType, unit)

      if (unit === 'mins') {
        text = `${prefix} - ${formattedAmount}`
      } else {
        text = `${prefix} - ${formattedAmount} ${unit}`
      }
    } else {
      text = prefix
    }
  } else if (
    amountStrategyType === 'contracts_variable_amount_strategy' &&
    isGraphQLType(amountStrategy, 'ContractsVariableAmountStrategy')
  ) {
    const prefix = i18n.t(
      'contract_versions.additional_compensation.amount_strategy_tag.variable_amount_strategy'
    )

    if (amountStrategy.upperLimit != null) {
      const upToText = i18n.t('contract_versions.additional_compensation.amount_strategy_tag.up_to')
      const formattedAmount = formatAmount(amountStrategy.upperLimit, unitType, unit)

      if (unit === 'mins') {
        text = `${prefix} - ${upToText} ${formattedAmount}`
      } else {
        text = `${prefix} - ${upToText} ${formattedAmount} ${unit}`
      }
    } else {
      text = `${prefix} - ${i18n.t(
        'contract_versions.additional_compensation.amount_strategy_tag.no_limit'
      )}`
    }
  } else if (
    amountStrategyType === PER_WORKED_DAY_STRATEGY &&
    isGraphQLType(amountStrategy, 'ContractsFixedAmountStrategy')
  ) {
    const prefix = i18n.t(
      'contract_versions.additional_compensation.amount_strategy_tag.per_worked_day_amount_strategy'
    )

    if (amountStrategy.amount != null) {
      const formattedAmount = formatAmount(amountStrategy.amount, unitType, unit)

      if (unit === 'mins') {
        text = `${prefix} - ${formattedAmount}`
      } else {
        text = `${prefix} - ${formattedAmount} ${unit}`
      }
    } else {
      text = prefix
    }
  }

  return (
    <Tag type="neutral" inline leftItem="circle">
      {text}
    </Tag>
  )
}

export { AmountStrategyTag }
